import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../stores/types/types';
import { ConnectedUser } from '../stores/types/connection';
import { AttachMask, UpsertMaskEntity } from '../connection/masks';
import { EntityType } from '../types/enums';
import { loadMasks } from '../stores/actions/masks';
import { closeMetaMenu } from '../stores/actions/tabs';
import { ProfileInterface, SafeProfileInterface } from '../stores/types/user-profiles';
import { NewProfileMask, ProfileMask } from '../stores/types/masks';
import logger from '../utils/logger-rum';

interface UseEditProfileMask {
	saveProfileMask: (fields: Partial<ConnectedUser>) => void;
	detachMask: (email: string) => Promise<void>;
	attachMask: (email: string, maskId: number) => Promise<void>;
}

interface Props {
	watchedProfile?: Partial<ProfileInterface> | null;
}

export default function useEditProfileMask({
	watchedProfile,
}: Props): UseEditProfileMask {
	const dispatch = useDispatch();
	const channel = useSelector((s: GS) => s.SessionReducer.meeting?.channel);
	const room = useSelector((s: GS) => s.SessionReducer.meeting?.meeting);
	const activeMasks = useSelector((s: GS) => s.MasksReducer.activeMasksEntity?.entityValue ?? {});
	const profileMaskEntities = useSelector((s: GS) => s.MasksReducer?.profileMaskEntities ?? []);
	const profileMasks = useSelector((s: GS) => s.MasksReducer.profileMasks);

	const existingId = activeMasks[watchedProfile?.email || ''];
	const entityId = profileMaskEntities.find((e) => e.entityValue.id === existingId)?.entityId ?? '';
	
	const saveProfileMask = useCallback(async (fields: Partial<SafeProfileInterface>) => {
		if (!room || !channel || !fields.email) {
			// putting here in case something goes horribly wrong
			logger.error('NO email when creating entity')
			return;
		}
		
		const safeProfile: Partial<SafeProfileInterface> = {}
		safeProfile.email = fields.email;

		// being extra cautious to only add the safe profile + channel/id to entity
		if (fields.first_name || fields.first_name === '')
			safeProfile.first_name = fields.first_name;
		if (fields.last_name || fields.last_name === '')
			safeProfile.last_name = fields.last_name;
		if (fields.avatar || fields.avatar === '')
			safeProfile.avatar = fields.avatar;
		if (fields.bg_image  || fields.bg_image === '')
			safeProfile.bg_image = fields.bg_image;
		if (fields.location  || fields.location === '')
			safeProfile.location = fields.location;
		if (fields.title  || fields.title === '')
			safeProfile.title = fields.title;
		if (fields.phone || fields.phone === '')
			safeProfile.phone = fields.phone;

		const entityValue: NewProfileMask = {...safeProfile, channel};

		if (existingId) {
			(entityValue as ProfileMask).id = existingId;
		}

		await UpsertMaskEntity(room, {
			entityId,
			entityType: EntityType.profileMask,
			entityValue,
		})
	}, [room, channel, existingId, entityId]);

	const detachMask = useCallback(async (email: string) => {
		const oldMask = profileMasks[activeMasks[email]];
		if (!room || !oldMask) {
			logger.error('No room or mask to detach');
			return;
		};
		await AttachMask(room, oldMask.id, email, true);
		dispatch(loadMasks(room));
		dispatch(closeMetaMenu());
	}, [room, profileMasks, activeMasks]);
	
	const attachMask = useCallback(async (email: string, maskId: number) => {
		if (!room || !maskId) {
			logger.error('No room or mask to attach');
			return;
		}
		await AttachMask(room, maskId, email, false);
		dispatch(loadMasks(room));
	}, [room]);

    return {
		saveProfileMask,
		attachMask,
		detachMask
	};
};